import React, {ChangeEvent, useCallback, useEffect, useRef} from 'react';

import {CommonInputInterface} from '../common-input-interface';
import {useValue, useFocus} from '../input-hooks';
import {Field} from '../field';
import {CrossIcon} from '../../svg-icon';
import {Button} from '../button';
import {cls, useUniqueId} from '../../../utils';

import css from './input.module.css';
import fieldCss from '../field/field.module.css';

interface InputInterface extends CommonInputInterface<string> {
	setRef?: React.RefObject<HTMLInputElement | null>;
	/** иконка или другой элемент перед текстом внутри рамки */
	contentBefore?: React.ReactNode;
	/** класс самой рамки поля, без подписи и подсказок */
	boxClassName?: string;
	ref?: React.Ref<HTMLInputElement>;
}

export type InputProps = InputInterface & Omit<React.InputHTMLAttributes<HTMLInputElement>, keyof InputInterface>;

export const Input: React.FC<InputProps> = (props) => {
	const {
		error,
		disabled,
		noButtons,
		buttons,
		className,
		bottomText,
		helperText,
		onChange: _,
		setRef,
		appearance,
		size,
		shape,
		label,
		disableClear,
		contentBefore,
		boxClassName,
		ref: forwardedRef,
		...inputProps
	} = props;
	const {hasFocus, onBlur, onFocus} = useFocus(props);
	const {value, onChange} = useValue(props);
	const handleClearValue = useCallback(() => {
		onChange({} as ChangeEvent<HTMLInputElement>);
	}, [onChange]);

	const id = useUniqueId('Input');
	const hasError = props.error && props.error !== true;

	const defaultRef = useRef<HTMLInputElement>(null);
	const inputRef = setRef || defaultRef;

	const handleRef = useCallback(
		(element: HTMLInputElement | null) => {
			inputRef.current = element;
			if (typeof forwardedRef === 'function') forwardedRef(element);
			else if (forwardedRef) forwardedRef.current = element;
		},
		[inputRef, forwardedRef],
	);

	useEffect(() => {
		if (inputRef.current && inputProps.autoFocus) inputRef.current.focus();
	}, [inputProps.autoFocus]);

	const clearButton = value && !disabled && !disableClear && (
		<Button
			appearance="subtle"
			icon={<CrossIcon width={14} height={14} />}
			onClick={handleClearValue}
			className={fieldCss.actionButton}
		/>
	);

	return (
		<Field
			commonProps={props}
			hasFocus={hasFocus}
			appearance={appearance ?? 'outline'}
			size={size}
			shape={shape}
			contentBefore={contentBefore}
			boxClassName={boxClassName}
			inputId={id}
			// пустой фрагмент всё равно считается за кнопки, и поле получает лишний отступ справа
			buttons={
				clearButton || buttons ? (
					<>
						{clearButton}
						{buttons}
					</>
				) : undefined
			}
			topLabel={!!value || hasFocus}
			className={className}
		>
			<input
				id={id}
				disabled={disabled}
				className={cls(
					css.input,
					!!hasError && css.hasError,
					hasFocus && css.hasFocus,
					disabled && css.disabled,
				)}
				{...(inputProps as any)}
				onChange={onChange}
				onBlur={onBlur}
				onFocus={onFocus}
				ref={handleRef}
				value={value || ''}
			/>
		</Field>
	);
};
